import React, { memo, useCallback, useContext } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { TURN_STATUS } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import Composer from 'Pods/ThreeDStudio/Shared/Composer/Composer'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setAspectRatio, setModelId, setVariations } from 'Pods/ThreeDStudio/Store/actions/imageSettings'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import Conversation from './Components/Conversation/Conversation'
import GenerationSettings from './Components/GenerationSettings/GenerationSettings'
import ModelSelector from './Components/ModelSelector/ModelSelector'
import NewImageNote from './Components/NewImageNote/NewImageNote'
import PrivateToggle from './Components/PrivateToggle/PrivateToggle'
import StyleButton from './Components/StyleButton/StyleButton'
import useCollapsedControls from './useCollapsedControls'
import useCreateTab from './useCreateTab'

const selectImageSettings = state => state.imageSettings

// The settings popover reports a change by setting key; each key has its own store action.
const SETTING_ACTIONS = {
  aspectRatio: setAspectRatio,
  variations: setVariations,
}

// isFullWidth renders the tab as the Image Generator page rather than inside the assistant
// panel: the same conversation and composer, laid out across the whole content area.
function CreateTab({ isFullWidth = false }) {
  const t = useTranslation()
  const { twoDImagesIdeationFlowEnabled, unfinishedTwoDFeaturesEnabled } = useContext(ThreeDStudioPageContext)
  // The composer settings live in the store, shared by the panel and the Image Generator
  // page. Without the ideation flow the composer shows none of the controls, so its runs
  // keep the backend's provider ranking, default aspect ratio and single image.
  const imageSettings = useThreeDStudioStore(selectImageSettings)
  const hasGenerationSettings = Boolean(twoDImagesIdeationFlowEnabled)
  const {
    prompt,
    attachments,
    messages,
    tags,
    isGenerating,
    isAccessPending,
    isUploadDisabled,
    fileInputRef,
    handlePromptChange,
    handleTagClick,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleRegenerate,
    handleGenerateAgain,
    handleApply,
    handleEdit,
  } = useCreateTab({ isFullWidth, hasGenerationSettings })
  const handleSettingsChange = useCallback((key, value) => {
    SETTING_ACTIONS[key]?.(value)
  }, [])

  // Model, Style and Private. When the row runs out of room they move into the settings
  // popover from the end, so Private collapses first and the model selector last.
  const buildControls = (isStacked) => [
    <ModelSelector key="model" value={imageSettings.modelId} onChange={setModelId} isStacked={isStacked} />,
    unfinishedTwoDFeaturesEnabled && <StyleButton key="style" isStacked={isStacked} />,
    <PrivateToggle key="private" isStacked={isStacked} />,
  ].filter(Boolean)
  const rowControls = buildControls(false)
  const { visibleCount, isMeasured, controlRefs } = useCollapsedControls(rowControls.length)
  const stackedControls = buildControls(true).slice(visibleCount)

  // Collapsed controls stay in the row as hidden, inert copies so their widths can
  // still be measured to decide when they fit again. Until the first measurement every
  // control but the first is pending: the server HTML cannot know the row's width, so
  // below 1280px, where the row runs out of room, those wait out of flow rather than
  // overflow the composer.
  const composerControls = (
    <>
      {rowControls.map((control, index) => {
        const isCollapsed = index >= visibleCount
        return (
          <div
            key={control.key}
            ref={controlRefs[index]}
            className={classnames(
              'image-assistant-create-tab__control',
              isCollapsed && 'image-assistant-create-tab__control--measure-only',
              !isMeasured && index > 0 && 'image-assistant-create-tab__control--pending',
            )}
            aria-hidden={isCollapsed || undefined}
            inert={isCollapsed ? '' : undefined}
          >
            {control}
          </div>
        )
      })}
      <GenerationSettings
        settings={imageSettings}
        stackedControls={stackedControls.length > 0 ? stackedControls : null}
        onChange={handleSettingsChange}
      />
    </>
  )

  // A send while the access tier is still hydrating cannot be judged, so the guard in
  // handleSend would swallow it; the button waits instead.
  const isSendDisabled = isGenerating || isAccessPending || !prompt.trim()
  const hasGeneratedImage = messages.some(message => message.status === TURN_STATUS.DONE && message.results.length > 0)

  return (
    <div
      className={classnames(
        'image-assistant-create-tab__create',
        isFullWidth && 'image-assistant-create-tab__create--full-width',
      )}
    >
      <Conversation
        messages={messages}
        isGenerating={isGenerating}
        onApply={handleApply}
        onRegenerate={handleRegenerate}
        onGenerateAgain={handleGenerateAgain}
        onEdit={handleEdit}
        isFullWidth={isFullWidth}
      />
      <Composer
        prompt={prompt}
        attachments={attachments}
        tags={tags}
        fileInputRef={fileInputRef}
        customClass={isFullWidth ? 'image-assistant-create-tab__composer--full-width' : ''}
        sendLabel={t('imageAssistantPanel.createTab.composer.send')}
        notice={hasGeneratedImage ? <NewImageNote /> : null}
        controls={twoDImagesIdeationFlowEnabled ? composerControls : null}
        uploadLabel={twoDImagesIdeationFlowEnabled ? t('imageAssistantPanel.createTab.composer.addReferences') : null}
        isSendDisabled={isSendDisabled}
        isUploadDisabled={isUploadDisabled}
        onPromptChange={handlePromptChange}
        onTagClick={handleTagClick}
        onUploadClick={handleUploadClick}
        onRemoveAttachment={handleRemoveAttachment}
        onFilesChange={handleFilesChange}
        onSend={handleSend}
      />
    </div>
  )
}

CreateTab.propTypes = {
  isFullWidth: bool,
}

export default memo(CreateTab)
